/**
 * Workspace-wide constants. Enum-shaped string union types that both
 * backend and frontend reference. The Prisma schema mirrors these as
 * native Postgres enums; keep both sides in sync when extending.
 *
 * Reference: spec section 4 (data model).
 */

/** Task status column on the Kanban board. Round 3 PR #36 renamed
 * THIS_WEEK to TODAY so the column reads as the day's plan rather
 * than a sprawling week of work; smart defaults fold the prior
 * 1-7 day bucket into BACKLOG. */
export const TASK_STATUS = ['BACKLOG', 'TODAY', 'DOING', 'WAITING', 'DONE'] as const;
export type TaskStatus = (typeof TASK_STATUS)[number];

/** Task type chip and swimlane partition. */
export const TASK_TYPE = ['SALES', 'ADMIN', 'FINANCE', 'PROJECT', 'GENERAL', 'PERSONAL'] as const;
export type TaskType = (typeof TASK_TYPE)[number];

/** Task priority bucket. */
export const TASK_PRIORITY = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const;
export type TaskPriority = (typeof TASK_PRIORITY)[number];

/** How a record entered the system. AI_* values are reserved for the
 * future trawler (spec section 19.1). `SALES_QUEUE` (UAT round 2 PR #32)
 * marks rows the spawn driver creates from the daily sales queue so the
 * Tasks board highlights them and the complete hook pushes the linked
 * lead or opportunity's nextActionDue forward. */
export const TASK_SOURCE = [
  'MANUAL',
  'AI_EMAIL',
  'AI_SLACK',
  'AI_XERO',
  'AI_CLAUDE_SUGGESTION',
  'AI_RESEARCH',
  'SALES_ACTION_SPAWN',
  'RECURRING_TEMPLATE',
  'SALES_QUEUE',
] as const;
export type TaskSource = (typeof TASK_SOURCE)[number];

/** Lead pipeline stage. */
export const LEAD_STAGE = ['TARGET', 'ACTIVE', 'CONVERTED', 'DEAD'] as const;
export type LeadStage = (typeof LEAD_STAGE)[number];

/** Opportunity pipeline stage (separate from lead stage). */
export const OPPORTUNITY_STAGE = ['NEW', 'ACTIVE', 'AWAITING', 'WON', 'LOST'] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGE)[number];

/** Current step within an opportunity; drives the suggested stage and win
 * probability (spec section 5.7). */
export const CURRENT_STEP = [
  'ACKNOWLEDGE',
  'MEET',
  'DEFINE_PROJECT',
  'DRAFT_PROPOSAL',
  'SEND_PROPOSAL',
  'AWAIT_DECISION',
  'DECIDE',
] as const;
export type CurrentStep = (typeof CURRENT_STEP)[number];

/** Win probability bucket. The numeric percentage lives only in the UI
 * label; the canonical value is the bucket name. */
export const WIN_PROBABILITY = ['COLD', 'MAYBE', 'LIKELY', 'STRONG', 'LOCKED'] as const;
export type WinProbability = (typeof WIN_PROBABILITY)[number];

/** Lead source channel. */
export const LEAD_SOURCE = [
  'KNOWN_CONTACT',
  'REFERRAL',
  'INBOUND',
  'OUTBOUND',
  'EVENT',
  'TENDER',
  'GRANT',
  'PROJECT',
] as const;
export type LeadSource = (typeof LEAD_SOURCE)[number];

/** Lead `nextAction` enum mirroring the Postgres `NextAction` enum.
 * Drives the planned next-step select on the lead slide-over (PR #34
 * UAT-34 inline-editable lead fields). */
export const LEAD_NEXT_ACTION = [
  'PLAN_OUTREACH',
  'SEND_OUTREACH',
  'FOLLOW_UP',
  'BOOK_MEETING',
  'HOLD_MEETING',
  'SEND_POST_MEETING_FOLLOW_UP',
  'QUALIFY',
  'NONE',
] as const;
export type LeadNextAction = (typeof LEAD_NEXT_ACTION)[number];

/** Opportunity capability tag. Free-form string in the data model but
 * the UI restricts to this set. */
export const CAPABILITY = ['AI', 'XR', 'SIMULATION', 'DIGITAL_TWIN'] as const;
export type Capability = (typeof CAPABILITY)[number];

/** Customer type bucket used by both leads and opportunities. */
export const CUSTOMER_TYPE = ['ENTERPRISE', 'GOVERNMENT', 'SMB', 'UNIVERSITY'] as const;
export type CustomerType = (typeof CUSTOMER_TYPE)[number];

/** Opportunity type tag. */
export const OPP_TYPE = ['PROPOSAL', 'TENDER', 'GRANT', 'VARIATION', 'OTHER'] as const;
export type OppType = (typeof OPP_TYPE)[number];

/** Budget-signal strength on an opportunity. */
export const BUDGET_SIGNAL = ['UNKNOWN', 'WEAK', 'MEDIUM', 'STRONG', 'CONFIRMED'] as const;
export type BudgetSignal = (typeof BUDGET_SIGNAL)[number];

/** Customer-status bucket on an opportunity. */
export const CUSTOMER_STATUS = ['NEW_CUSTOMER', 'EXISTING_CUSTOMER', 'NEW_LEAD'] as const;
export type CustomerStatus = (typeof CUSTOMER_STATUS)[number];

// ────────────────────────────────────────────────────────────────────
// Leads & Opportunities — Current/Next Action model (Step 1)
//
// Both leads and opportunities move from a single next-action / current-
// step field to a Current Action (canonical, user-set) plus a derived
// Next Action and a derived stage. The lookup tables below are the single
// source of truth shared by the backend domain layer and the frontend
// (which renders the derived Next Action without a round-trip).
//
// Reference: docs/plans/leads-opps-step-1.md (brief tables 1 and 2).
// ────────────────────────────────────────────────────────────────────

/** Industry vertical for a lead. Ten common industries plus Other.
 * Replaces the former free-text `industryVertical` field. */
export const INDUSTRY = [
  'MINING_RESOURCES',
  'DEFENCE',
  'GOVERNMENT',
  'EDUCATION_RESEARCH',
  'HEALTHCARE',
  'MANUFACTURING',
  'CONSTRUCTION_ENGINEERING',
  'ENERGY_UTILITIES',
  'TRANSPORT_LOGISTICS',
  'FINANCIAL_SERVICES',
  'OTHER',
] as const;
export type Industry = (typeof INDUSTRY)[number];

/** Human display labels for the industry enum (sentence case, en-AU). */
export const INDUSTRY_LABELS: Record<Industry, string> = {
  MINING_RESOURCES: 'Mining & Resources',
  DEFENCE: 'Defence',
  GOVERNMENT: 'Government',
  EDUCATION_RESEARCH: 'Education & Research',
  HEALTHCARE: 'Healthcare',
  MANUFACTURING: 'Manufacturing',
  CONSTRUCTION_ENGINEERING: 'Construction & Engineering',
  ENERGY_UTILITIES: 'Energy & Utilities',
  TRANSPORT_LOGISTICS: 'Transport & Logistics',
  FINANCIAL_SERVICES: 'Financial Services',
  OTHER: 'Other',
};

/**
 * Lead Current Action — the canonical, user-set action state of a lead.
 * Next Action is derived via `NEXT_LEAD_ACTION`; stage is derived via
 * `LEAD_STAGE_FOR_ACTION`. `NONE` is the fresh-lead state (displayed as
 * "-"). `CONVERTED_TO_OPPORTUNITY` is reachable only through the Convert
 * button, never the dropdown.
 */
export const LEAD_ACTION = [
  'NONE',
  'PLAN_OUTREACH',
  'SEND_OUTREACH',
  'RESPONDED',
  'BOOK_MEETING',
  'MEETING_BOOKED',
  'POST_MEETING_FOLLOW_UP',
  'CONVERT_DISCARD',
  'CONVERTED_TO_OPPORTUNITY',
  'DISQUALIFIED',
  'HOLD_WAITING',
  'FOLLOW_UP',
  'RECYCLE',
  'NURTURING',
] as const;
export type LeadAction = (typeof LEAD_ACTION)[number];

/** Human display labels for the lead-action enum. */
export const LEAD_ACTION_LABELS: Record<LeadAction, string> = {
  NONE: '-',
  PLAN_OUTREACH: 'Plan Outreach',
  SEND_OUTREACH: 'Send Outreach',
  RESPONDED: 'Responded',
  BOOK_MEETING: 'Book Meeting',
  MEETING_BOOKED: 'Meeting Booked',
  POST_MEETING_FOLLOW_UP: 'Post-Meeting Follow-Up',
  CONVERT_DISCARD: 'Convert/Discard',
  CONVERTED_TO_OPPORTUNITY: 'Converted to Opportunity',
  DISQUALIFIED: 'Disqualified',
  HOLD_WAITING: 'Hold/Waiting',
  FOLLOW_UP: 'Follow-Up',
  RECYCLE: 'Recycle',
  NURTURING: 'Nurturing',
};

/**
 * Lead-action values the user may pick directly from the Current Action
 * dropdown. Excludes `CONVERTED_TO_OPPORTUNITY` (Convert button only) and
 * `NONE` (the initial fresh state, not re-selectable).
 */
export const LEAD_ACTION_SELECTABLE: LeadAction[] = LEAD_ACTION.filter(
  (a) => a !== 'CONVERTED_TO_OPPORTUNITY' && a !== 'NONE',
);

/**
 * Derived Next Action for each Lead Current Action (brief table 1).
 * `null` marks a terminal or holding state with no automatic next step.
 */
export const NEXT_LEAD_ACTION: Record<LeadAction, LeadAction | null> = {
  NONE: 'PLAN_OUTREACH',
  PLAN_OUTREACH: 'SEND_OUTREACH',
  SEND_OUTREACH: 'RESPONDED',
  RESPONDED: 'BOOK_MEETING',
  BOOK_MEETING: 'MEETING_BOOKED',
  MEETING_BOOKED: 'POST_MEETING_FOLLOW_UP',
  POST_MEETING_FOLLOW_UP: 'CONVERT_DISCARD',
  CONVERT_DISCARD: 'CONVERTED_TO_OPPORTUNITY',
  CONVERTED_TO_OPPORTUNITY: null,
  DISQUALIFIED: null,
  HOLD_WAITING: null,
  FOLLOW_UP: null,
  RECYCLE: null,
  NURTURING: null,
};

/**
 * Stage a lead lands in for each Current Action. `null` means "leave the
 * stage unchanged" — the holding states (Hold/Waiting, Follow-Up,
 * Nurturing) keep the lead in its current working stage rather than
 * moving it. Reference: brief section 1.3.
 */
export const LEAD_STAGE_FOR_ACTION: Record<LeadAction, LeadStage | null> = {
  NONE: 'TARGET',
  PLAN_OUTREACH: 'TARGET',
  SEND_OUTREACH: 'TARGET',
  RESPONDED: 'ACTIVE',
  BOOK_MEETING: 'ACTIVE',
  MEETING_BOOKED: 'ACTIVE',
  POST_MEETING_FOLLOW_UP: 'ACTIVE',
  CONVERT_DISCARD: 'ACTIVE',
  CONVERTED_TO_OPPORTUNITY: 'CONVERTED',
  DISQUALIFIED: 'DEAD',
  RECYCLE: 'TARGET',
  HOLD_WAITING: null,
  FOLLOW_UP: null,
  NURTURING: null,
};

/**
 * Opportunity Current Action — the canonical, user-set action state of an
 * opportunity. Next Action derives via `NEXT_OPPORTUNITY_ACTION`; stage
 * derives via `OPPORTUNITY_STAGE_FOR_ACTION`. `WON` / `LOST` are reachable
 * only through the Mark Won / Mark Lost buttons, never the dropdown.
 */
export const OPPORTUNITY_ACTION = [
  'FRESH_LEAD',
  'REQUEST_MEETING',
  'BOOKED_MEETING',
  'COMPLETED_MEETING',
  'EMAIL_CONFIRMATION_ON_PROPOSAL',
  'DRAFT_PROPOSAL',
  'SEND_PROPOSAL',
  'WAIT_ON_DECISION',
  'UPDATE_PROPOSAL',
  'HOLD_WAITING',
  'FOLLOW_UP',
  'NURTURING',
  'WON',
  'LOST',
] as const;
export type OpportunityAction = (typeof OPPORTUNITY_ACTION)[number];

/** Human display labels for the opportunity-action enum. */
export const OPPORTUNITY_ACTION_LABELS: Record<OpportunityAction, string> = {
  FRESH_LEAD: 'Fresh Lead',
  REQUEST_MEETING: 'Request Meeting',
  BOOKED_MEETING: 'Booked Meeting',
  COMPLETED_MEETING: 'Completed Meeting',
  EMAIL_CONFIRMATION_ON_PROPOSAL: 'Email Confirmation on Proposal',
  DRAFT_PROPOSAL: 'Draft Proposal',
  SEND_PROPOSAL: 'Send Proposal',
  WAIT_ON_DECISION: 'Wait on Decision',
  UPDATE_PROPOSAL: 'Update Proposal',
  HOLD_WAITING: 'Hold/Waiting',
  FOLLOW_UP: 'Follow-Up',
  NURTURING: 'Nurturing',
  WON: 'Won',
  LOST: 'Lost',
};

/**
 * Opportunity-action values selectable from the Current Action dropdown.
 * Excludes `WON` / `LOST` (Mark Won / Mark Lost buttons only) and
 * `FRESH_LEAD` (the initial state on conversion, not re-selectable).
 */
export const OPPORTUNITY_ACTION_SELECTABLE: OpportunityAction[] = OPPORTUNITY_ACTION.filter(
  (a) => a !== 'WON' && a !== 'LOST' && a !== 'FRESH_LEAD',
);

/**
 * Derived Next Action for each Opportunity Current Action (brief table 2).
 * `null` marks a terminal or holding state.
 */
export const NEXT_OPPORTUNITY_ACTION: Record<OpportunityAction, OpportunityAction | null> = {
  FRESH_LEAD: 'REQUEST_MEETING',
  REQUEST_MEETING: 'BOOKED_MEETING',
  BOOKED_MEETING: 'COMPLETED_MEETING',
  COMPLETED_MEETING: 'EMAIL_CONFIRMATION_ON_PROPOSAL',
  EMAIL_CONFIRMATION_ON_PROPOSAL: 'DRAFT_PROPOSAL',
  DRAFT_PROPOSAL: 'SEND_PROPOSAL',
  SEND_PROPOSAL: 'WAIT_ON_DECISION',
  WAIT_ON_DECISION: null,
  UPDATE_PROPOSAL: null,
  HOLD_WAITING: null,
  FOLLOW_UP: null,
  NURTURING: null,
  WON: null,
  LOST: null,
};

/**
 * Stage an opportunity lands in for each Current Action (brief section
 * 1.6). Every opportunity action maps to a concrete stage (no holding
 * "leave unchanged" case, unlike leads).
 */
export const OPPORTUNITY_STAGE_FOR_ACTION: Record<OpportunityAction, OpportunityStage> = {
  FRESH_LEAD: 'NEW',
  REQUEST_MEETING: 'ACTIVE',
  BOOKED_MEETING: 'ACTIVE',
  COMPLETED_MEETING: 'ACTIVE',
  EMAIL_CONFIRMATION_ON_PROPOSAL: 'ACTIVE',
  DRAFT_PROPOSAL: 'ACTIVE',
  SEND_PROPOSAL: 'ACTIVE',
  WAIT_ON_DECISION: 'AWAITING',
  UPDATE_PROPOSAL: 'AWAITING',
  HOLD_WAITING: 'AWAITING',
  FOLLOW_UP: 'AWAITING',
  NURTURING: 'AWAITING',
  WON: 'WON',
  LOST: 'LOST',
};

// ────────────────────────────────────────────────────────────────────
// Current Action colour coding + ordering (Tabs-updates batch)
//
// Every Current Action gets its own bright, distinct colour so the Kanban
// pill reads at a glance. Stored as hex pairs (background + foreground) and
// applied via inline style, so Tailwind's purge never strips a
// dynamically-built class. Overlapping action names (Follow-Up,
// Hold/Waiting, Nurturing) share a hue across leads and opps; terminal
// states follow convention (green won/converted, red disqualified/lost).
//
// Reference: docs/plans/leads-opps-tabs-updates.md section 2.6.
// ────────────────────────────────────────────────────────────────────

/** A pill colour: solid background with a legible foreground. */
export interface ActionColor {
  bg: string;
  fg: string;
}

const WHITE = '#FFFFFF';

/** Bright per-action colours for leads. */
export const LEAD_ACTION_COLORS: Record<LeadAction, ActionColor> = {
  NONE: { bg: '#64748B', fg: WHITE },
  PLAN_OUTREACH: { bg: '#2563EB', fg: WHITE },
  SEND_OUTREACH: { bg: '#4F46E5', fg: WHITE },
  RESPONDED: { bg: '#0891B2', fg: WHITE },
  BOOK_MEETING: { bg: '#0D9488', fg: WHITE },
  MEETING_BOOKED: { bg: '#7C3AED', fg: WHITE },
  POST_MEETING_FOLLOW_UP: { bg: '#C026D3', fg: WHITE },
  CONVERT_DISCARD: { bg: '#D97706', fg: WHITE },
  CONVERTED_TO_OPPORTUNITY: { bg: '#16A34A', fg: WHITE },
  DISQUALIFIED: { bg: '#DC2626', fg: WHITE },
  HOLD_WAITING: { bg: '#71717A', fg: WHITE },
  FOLLOW_UP: { bg: '#0284C7', fg: WHITE },
  RECYCLE: { bg: '#65A30D', fg: WHITE },
  NURTURING: { bg: '#9333EA', fg: WHITE },
};

/** Bright per-action colours for opportunities. */
export const OPPORTUNITY_ACTION_COLORS: Record<OpportunityAction, ActionColor> = {
  FRESH_LEAD: { bg: '#64748B', fg: WHITE },
  REQUEST_MEETING: { bg: '#2563EB', fg: WHITE },
  BOOKED_MEETING: { bg: '#0891B2', fg: WHITE },
  COMPLETED_MEETING: { bg: '#0D9488', fg: WHITE },
  EMAIL_CONFIRMATION_ON_PROPOSAL: { bg: '#7C3AED', fg: WHITE },
  DRAFT_PROPOSAL: { bg: '#D97706', fg: WHITE },
  SEND_PROPOSAL: { bg: '#EA580C', fg: WHITE },
  WAIT_ON_DECISION: { bg: '#4F46E5', fg: WHITE },
  UPDATE_PROPOSAL: { bg: '#C026D3', fg: WHITE },
  HOLD_WAITING: { bg: '#71717A', fg: WHITE },
  FOLLOW_UP: { bg: '#0284C7', fg: WHITE },
  NURTURING: { bg: '#9333EA', fg: WHITE },
  WON: { bg: '#16A34A', fg: WHITE },
  LOST: { bg: '#DC2626', fg: WHITE },
};

/** Sort index of a lead action = its position in the dropdown order. */
export function leadActionOrder(action: LeadAction): number {
  const i = LEAD_ACTION.indexOf(action);
  return i === -1 ? LEAD_ACTION.length : i;
}

/** Sort index of an opportunity action = its position in the dropdown order. */
export function opportunityActionOrder(action: OpportunityAction): number {
  const i = OPPORTUNITY_ACTION.indexOf(action);
  return i === -1 ? OPPORTUNITY_ACTION.length : i;
}

/** API version prefix; bump if a breaking change is shipped. */
export const API_VERSION = 'v1' as const;

/** Default Brisbane timezone used for end-of-day rollups and the morning
 * brief assembly. */
export const DEFAULT_TIMEZONE = 'Australia/Brisbane' as const;
